import { Dialog } from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import React, { useEffect, useReducer, useState } from "react";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Dropzone from "react-dropzone";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import { useChunkedUpload } from "hooks/useChunkedUpload";
import { useSnackbar } from "notistack";
import { FileFlat } from "models/FileFlat";
import { CloseButton, GroupAutocomplete } from './UploadFileDialog.styles';
import {
    SectionContainer,
    DropzoneContainer,
    UploadingFilesContainer,
    UploadingFileItem,
    FileItemContent,
    FileItemRow,
    FileNameContainer,
    FileStatusContainer,
    WaitingStatus,
    UploadingStatus,
    SuccessStatus,
    FailedStatus,
    ProgressContainer,
    ProgressBar
} from './UploadFileDialog.styles';
import { ResourceType } from "models/ResourceType";

interface UploadFileDialogProps {
    editor: any;
    onClose: () => void;
    onFilesUploaded: (uploadedFiles: FileFlat[], allSucceed: boolean) => void;
    groups: string[];
    resourceType: ResourceType;
}

interface UploadingFileModel {
    name: string;
    progress: number;
    uploadState: UploadState;
}

enum UploadState {
    Waiting,
    Uploading,
    Success,
    Failed
}

const ACTIONS = {
    SET_UPLOADING_FILES: "set_uploading_files",
    UPDATE_PROGRESS: "update_progress",
    UPDATE_STATE: "update_state",
}

function reducer(uploadingFiles: Map<number, UploadingFileModel>, action: any): Map<number, UploadingFileModel> {
    switch (action.type) {
        case ACTIONS.SET_UPLOADING_FILES:
            return action.payload.uploadingFiles;
        case ACTIONS.UPDATE_PROGRESS: {
            const id = action.payload.id;
            const file = uploadingFiles.get(id);
            if (file) {
                file.progress = action.payload.progress;
                uploadingFiles.set(id, file);
                return new Map(uploadingFiles);
            }
            return uploadingFiles;
        }
        case ACTIONS.UPDATE_STATE: {
            const id = action.payload.id;
            const file = uploadingFiles.get(id);
            if (file) {
                file.uploadState = action.payload.state;
                uploadingFiles.set(id, file);
                return new Map(uploadingFiles);
            }
            return uploadingFiles;
        }
        default:
            return uploadingFiles;
    }
}

export const UploadFileDialog = (props: UploadFileDialogProps) => {

    const { upload } = useChunkedUpload();
    const [uploading, setUploading] = useState(false);
    const [showUploadingFiles, setShowUploadingFiles] = useState(false);
    const [uploadingFiles, dispatch] = useReducer(reducer, new Map<number, UploadingFileModel>());
    const storage = props.editor.storage;
    const [selectedGroup, setSelectedGroup] = useState<string | null>(storage.getSelectedGroup(props.resourceType));
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    let mimeTypes: string;
    switch (props.resourceType) {
        case ResourceType.NodeImage:
            mimeTypes = "image/jpeg, image/png, image/jpg";
            break;
        case ResourceType.NodeVideo:
            mimeTypes = "video/mp4, video/x-matroska, video/matroska";
            break;
        case ResourceType.NodeAudio:
            mimeTypes = "audio/mpeg";
            break;
        case ResourceType.NodeInfoIcon:
            mimeTypes = "image/png";
            break;
        case ResourceType.AvatarAnimation:
            mimeTypes = "model/gltf-binary, .glb";
            break;
        default:
            throw new Error("Unhandled node file type!");
    }

    const handleFilesDrop = async (acceptedFiles: File[]) => {
        if (uploading) return;
        setUploading(true);

        let uploadingFiles: Map<number, UploadingFileModel> = new Map<number, UploadingFileModel>();
        acceptedFiles.forEach((acceptedFile, index) => {
            uploadingFiles.set(index, {
                name: acceptedFile.name,
                progress: 0,
                uploadState: UploadState.Waiting
            });
        });
        dispatch({ type: ACTIONS.SET_UPLOADING_FILES, payload: { uploadingFiles: uploadingFiles } });

        let uploadedFiles: FileFlat[] = [];

        setShowUploadingFiles(true);

        for (let index = 0; index < acceptedFiles.length; index++) {
            const acceptedFile = acceptedFiles[index];
            const group = selectedGroup && selectedGroup.length > 0 ? selectedGroup : null;

            dispatch({ type: ACTIONS.UPDATE_STATE, payload: { id: index, state: UploadState.Uploading } });

            const result = await upload(
                acceptedFile,
                props.resourceType,
                group,
                (percentage: number) => {
                    if (percentage <= 99) {
                        dispatch({ type: ACTIONS.UPDATE_PROGRESS, payload: { id: index, progress: percentage } });
                    }
                }
            );

            dispatch({
                type: ACTIONS.UPDATE_STATE,
                payload: {
                    id: index,
                    state: result.success ? UploadState.Success : UploadState.Failed
                }
            });
            dispatch({ type: ACTIONS.UPDATE_PROGRESS, payload: { id: index, progress: 100 } });

            if (result.success && result.data) {
                uploadedFiles.push({
                    id: result.data.fileId,
                    fileName: result.data.fileName,
                    group: result.data.group ?? null,
                } as FileFlat);
            } else if (result.errors?.length) {
                result.errors.forEach((error) => {
                    enqueueSnackbar(t(`api.${error.code}`), { variant: 'error' });
                });
            }
        }

        props.onFilesUploaded(uploadedFiles, uploadedFiles.length === acceptedFiles.length);

        setUploading(false);
    }

    const handleGroupChange = (event: React.SyntheticEvent, value: unknown, reason: any, details?: any) => {
        setSelectedGroup(value as string | null);
    }

    const handleGroupInputChange = (event: React.SyntheticEvent, value: string) => {
        setSelectedGroup(value);
    }

    useEffect(() => {
        storage.saveSelectedGroup(props.resourceType, selectedGroup);
    }, [props.resourceType, selectedGroup, storage]);

    return (
        <Dialog open={true}>
            <DialogTitle>{t("nodeEditor.uploadFiles")}</DialogTitle>
            <CloseButton onClick={props.onClose}>
                <CloseIcon/>
            </CloseButton>
            <DialogContent>
                <Dropzone onDrop={acceptedFiles => handleFilesDrop(acceptedFiles)} accept={mimeTypes}>
                    {({ acceptedFiles, getRootProps, getInputProps }) => (
                        <SectionContainer>
                            <GroupAutocomplete
                                freeSolo
                                size="small"
                                value={selectedGroup}
                                options={props.groups}
                                onChange={handleGroupChange}
                                onInputChange={handleGroupInputChange}
                                renderInput={(params) => (
                                    <TextField {...params} label={t("nodeEditor.group")}/>
                                )}
                                ListboxProps={{ style: { scrollbarWidth: "thin" } }}
                            />
                            <DropzoneContainer {...getRootProps()}>
                                <input {...getInputProps()} />
                                {props.resourceType === ResourceType.NodeImage &&
                                    <div>
                                        <p>{t("nodeEditor.dragAndDropImage")}</p>
                                        <em>{t("nodeEditor.onlyImageTypesAreAccepted")}</em>
                                    </div>
                                }
                                {props.resourceType === ResourceType.NodeVideo &&
                                    <div>
                                        <p>{t("nodeEditor.dragAndDropVideo")}</p>
                                        <em>{t("nodeEditor.onlyVideoTypesAreAccepted")}</em>
                                    </div>
                                }
                                {props.resourceType === ResourceType.NodeAudio &&
                                    <div>
                                        <p>{t("nodeEditor.dragAndDropAudio")}</p>
                                        <em>{t("nodeEditor.onlyAudioTypesAreAccepted")}</em>
                                    </div>
                                }
                                {props.resourceType === ResourceType.NodeInfoIcon &&
                                    <div>
                                        <p>{t("nodeEditor.dragAndDropInfoIcon")}</p>
                                        <em>{t("nodeEditor.onlyInfoIconTypesAreAccepted")}</em>
                                    </div>
                                }
                                {props.resourceType === ResourceType.AvatarAnimation &&
                                    <div>
                                        <p>{t("nodeEditor.dragAndDropAvatarAnimation")}</p>
                                        <em>{t("nodeEditor.onlyAvatarAnimationTypesAreAccepted")}</em>
                                    </div>
                                }
                            </DropzoneContainer>
                            <aside>
                                {showUploadingFiles &&
                                    <UploadingFilesContainer>
                                        {Array.from(uploadingFiles.entries()).map(([key, uploadingFile]) => (
                                            <UploadingFileItem key={key}>
                                                <FileItemContent>
                                                    <FileItemRow>
                                                        <FileNameContainer>
                                                            {`${key + 1}. ${uploadingFile.name}`}
                                                        </FileNameContainer>
                                                        <FileStatusContainer>
                                                            {uploadingFile.uploadState === UploadState.Waiting &&
                                                                <WaitingStatus>
                                                                    {`${t("nodeEditor.waiting")}...`}
                                                                </WaitingStatus>
                                                            }
                                                            {uploadingFile.uploadState === UploadState.Uploading &&
                                                                <UploadingStatus>
                                                                    {`${uploadingFile.progress}%`}
                                                                </UploadingStatus>
                                                            }
                                                            {uploadingFile.uploadState === UploadState.Success &&
                                                                <SuccessStatus>
                                                                    {t("nodeEditor.success")}
                                                                </SuccessStatus>
                                                            }
                                                            {uploadingFile.uploadState === UploadState.Failed &&
                                                                <FailedStatus>
                                                                    {t("nodeEditor.failed")}
                                                                </FailedStatus>
                                                            }
                                                        </FileStatusContainer>
                                                    </FileItemRow>
                                                    <ProgressContainer>
                                                        <ProgressBar progress={uploadingFile.progress}/>
                                                    </ProgressContainer>
                                                </FileItemContent>
                                            </UploadingFileItem>
                                        ))}
                                    </UploadingFilesContainer>
                                }
                            </aside>
                        </SectionContainer>
                    )}
                </Dropzone>
            </DialogContent>
        </Dialog>
    );
}